import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { UserPlus, Trash2, Pencil, Check, X, Eye, EyeOff, Shield, ShieldOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const modHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem("mod_token")}` });

const EMPTY_NEW = { email: "", name: "", password: "", role: "moderator" };

export default function UsersAdmin({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState(EMPTY_NEW);
  const [showPwd, setShowPwd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({ name: "", role: "moderator", password: "" });
  const [showEditPwd, setShowEditPwd] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/mod/users`, { headers: modHeaders() });
      setUsers(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Kunde inte hämta användare");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleCreate = async () => {
    setError("");
    try {
      await axios.post(`${API}/mod/users`, newUser, { headers: modHeaders() });
      setCreating(false);
      setNewUser(EMPTY_NEW);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || "Kunde inte skapa användare");
    }
  };

  const startEdit = (u) => {
    setEditId(u.id);
    setEditData({ name: u.name, role: u.role === "admin" ? "master" : u.role, password: "" });
    setShowEditPwd(false);
  };

  const handleSaveEdit = async (id) => {
    setError("");
    const payload = {
      name: editData.name,
      role: editData.role,
    };
    if (editData.password) payload.password = editData.password;
    try {
      await axios.put(`${API}/mod/users/${id}`, payload, { headers: modHeaders() });
      setEditId(null);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || "Kunde inte spara ändringar");
    }
  };

  const handleDelete = async (id, email) => {
    if (!window.confirm(`Ta bort användare ${email}?`)) return;
    setError("");
    try {
      await axios.delete(`${API}/mod/users/${id}`, { headers: modHeaders() });
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || "Kunde inte ta bort användaren");
    }
  };

  const roleLabel = (r) => (r === "master" || r === "admin") ? "Master" : "Moderator";

  return (
    <div data-testid="users-admin">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm text-muted-foreground">{users.length} användare</p>
        </div>
        <button
          data-testid="user-create-btn"
          onClick={() => { setCreating(true); setError(""); setNewUser(EMPTY_NEW); }}
          className="flex items-center gap-2 bg-falu hover:bg-falu-hover text-white text-sm font-medium px-4 py-2 rounded-md transition-colors"
        >
          <UserPlus size={16} /> Ny användare
        </button>
      </div>

      {error && (
        <div data-testid="users-error" className="bg-falu/10 border border-falu/30 text-falu text-sm px-3 py-2 rounded mb-4">
          {error}
        </div>
      )}

      {creating && (
        <div data-testid="user-create-form" className="bg-white border border-warm-border rounded-md p-4 mb-4">
          <h3 className="font-heading text-lg font-medium text-ink mb-3">Ny användare</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="E-post *">
              <input
                data-testid="new-user-email"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                className="w-full px-3 py-2 border border-warm-border rounded text-sm focus:border-pine focus:outline-none"
              />
            </Field>
            <Field label="Namn *">
              <input
                data-testid="new-user-name"
                type="text"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                className="w-full px-3 py-2 border border-warm-border rounded text-sm focus:border-pine focus:outline-none"
              />
            </Field>
            <Field label="Lösenord * (minst 8 tecken)">
              <div className="relative">
                <input
                  data-testid="new-user-password"
                  type={showPwd ? "text" : "password"}
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  className="w-full px-3 py-2 pr-10 border border-warm-border rounded text-sm focus:border-pine focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-bark hover:text-ink"
                  aria-label={showPwd ? "Dölj" : "Visa"}
                >
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </Field>
            <Field label="Roll">
              <select
                data-testid="new-user-role"
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                className="w-full px-3 py-2 border border-warm-border rounded text-sm bg-white focus:border-pine focus:outline-none"
              >
                <option value="moderator">Moderator (Anslag, Evenemang, Föreningar, Företag)</option>
                <option value="master">Master (allt)</option>
              </select>
            </Field>
          </div>
          <div className="flex gap-2 mt-4">
            <button
              data-testid="new-user-save"
              onClick={handleCreate}
              disabled={!newUser.email || !newUser.password || newUser.password.length < 8 || !newUser.name}
              className="flex items-center gap-2 bg-pine hover:bg-pine-hover disabled:opacity-40 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors"
            >
              <Check size={16} /> Skapa
            </button>
            <button
              onClick={() => { setCreating(false); setError(""); }}
              className="flex items-center gap-2 text-muted-foreground hover:text-ink text-sm px-4 py-2"
            >
              <X size={16} /> Avbryt
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-muted-foreground py-8">Laddar...</p>
      ) : (
        <div data-testid="users-list" className="flex flex-col gap-2">
          {users.map((u) => {
            const isEditing = editId === u.id;
            const isSelf = currentUser && currentUser.id === u.id;
            const master = u.role === "master" || u.role === "admin";
            return (
              <div
                key={u.id}
                data-testid={`user-row-${u.id}`}
                className="bg-white border border-warm-border rounded-md p-4"
              >
                {isEditing ? (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-bark mb-1">Namn</label>
                      <input
                        data-testid={`edit-name-${u.id}`}
                        type="text"
                        value={editData.name}
                        onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-warm-border rounded text-sm"
                      />
                      <p className="text-xs text-muted-foreground mt-1">{u.email}</p>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-bark mb-1">Roll</label>
                      <select
                        data-testid={`edit-role-${u.id}`}
                        value={editData.role}
                        onChange={(e) => setEditData({ ...editData, role: e.target.value })}
                        className="w-full px-3 py-2 border border-warm-border rounded text-sm bg-white"
                      >
                        <option value="moderator">Moderator</option>
                        <option value="master">Master</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-bark mb-1">Nytt lösenord</label>
                      <div className="relative">
                        <input
                          data-testid={`edit-password-${u.id}`}
                          type={showEditPwd ? "text" : "password"}
                          value={editData.password}
                          placeholder="Lämna tomt för att behålla"
                          onChange={(e) => setEditData({ ...editData, password: e.target.value })}
                          className="w-full px-3 py-2 pr-10 border border-warm-border rounded text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => setShowEditPwd((v) => !v)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-bark hover:text-ink"
                          aria-label={showEditPwd ? "Dölj" : "Visa"}
                        >
                          {showEditPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>
                    <div className="sm:col-span-3 flex gap-2">
                      <button
                        data-testid={`save-edit-${u.id}`}
                        onClick={() => handleSaveEdit(u.id)}
                        className="flex items-center gap-2 bg-pine hover:bg-pine-hover text-white text-sm px-4 py-2 rounded-md"
                      >
                        <Check size={16} /> Spara
                      </button>
                      <button
                        onClick={() => setEditId(null)}
                        className="flex items-center gap-2 text-muted-foreground hover:text-ink text-sm px-4 py-2"
                      >
                        <X size={16} /> Avbryt
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {master ? <Shield size={18} className="text-falu shrink-0" /> : <ShieldOff size={18} className="text-bark/40 shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-ink">{u.name}</p>
                          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-semibold ${master ? "bg-falu/10 text-falu" : "bg-pine/10 text-pine"}`}>
                            {roleLabel(u.role)}
                          </span>
                          {isSelf && <span className="text-[10px] text-muted-foreground">(Du)</span>}
                        </div>
                        <p className="text-sm text-muted-foreground truncate">{u.email}</p>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button
                        data-testid={`edit-user-${u.id}`}
                        onClick={() => startEdit(u)}
                        className="p-2 text-muted-foreground hover:text-ink"
                        title="Redigera"
                      >
                        <Pencil size={16} />
                      </button>
                      {!isSelf && (
                        <button
                          data-testid={`delete-user-${u.id}`}
                          onClick={() => handleDelete(u.id, u.email)}
                          className="p-2 text-muted-foreground hover:text-falu"
                          title="Ta bort"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-bark mb-1">{label}</label>
      {children}
    </div>
  );
}
